import React, { useState, useEffect, useCallback } from 'react';
import {
  Sun, Moon, LogOut, Users, LayoutDashboard,
  Settings, HelpCircle, Menu, Mail, History,
  Brain, Activity, Heart, AlertTriangle, Check,
  Play, Pause, RefreshCw, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, Clock, Zap, Eye,
  BarChart3, Shield, UserCheck, Bell, Target, DollarSign
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

function AutomationPage({ user, onLogout, theme, setTheme, setPage }) {
  const initials = user.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [section, setSection] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [status, setStatus] = useState(null);
  const [healthScores, setHealthScores] = useState([]);
  const [automationLogs, setAutomationLogs] = useState({ logs: [], total: 0 });
  const [automationRuns, setAutomationRuns] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState('');
  const detailRef = React.useRef(null);

  const loadStatus = useCallback(async () => {
    try { const r = await api.getAutomationStatus(); setStatus(r); } catch {}
  }, []);

  const loadHealthScores = useCallback(async () => {
    try { const r = await api.getHealthScores(); setHealthScores(r); } catch {}
  }, []);

  const loadLogs = useCallback(async (page = 1, campaign_type = '') => {
    try {
      const params = { page, limit: 25 };
      if (campaign_type) params.campaign_type = campaign_type;
      const r = await api.getAutomationLogs(params);
      setAutomationLogs(r);
    } catch {}
  }, []);

  const loadRuns = useCallback(async () => {
    try { const r = await api.getAutomationRuns(); setAutomationRuns(r); } catch {}
  }, []);

  const loadPreview = async () => {
    setPreviewLoading(true);
    try { const r = await api.getAutomationPreview(); setPreview(r); } catch {}
    setPreviewLoading(false);
  };

  const loadConversions = async () => {
    try {
      const [conv, jrn] = await Promise.all([api.getAutomationConversions(), api.getAutomationJourneys()]);
      setConversions(conv);
      setJourneys(jrn);
    } catch {}
  };

  const loadUserDetail = async (userId) => {
    setDetailLoading(true);
    setSelectedUser(userId);
    setUserDetail(null);
    try {
      const r = await api.getUserHealthScore(userId);
      setUserDetail(r);
      setTimeout(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      setError('Erro ao carregar detalhes: ' + (err.error || err.message || 'erro desconhecido'));
      setSelectedUser(null);
    }
    setDetailLoading(false);
  };

  useEffect(() => {
    loadStatus();
    loadHealthScores();
    loadLogs(1, '');
  }, [loadStatus, loadHealthScores, loadLogs]);

  const handleToggle = async () => {
    if (!status) return;
    setError(''); setSuccess('');
    try {
      await api.toggleAutomation(!status.enabled);
      setSuccess(status.enabled ? 'Automacao desativada' : 'Automacao ativada');
      loadStatus();
    } catch (err) {
      setError(err.error || 'Erro ao alterar automacao');
    }
  };

  const handleManualRun = async (type) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      await api.runAutomation(type);
      setSuccess('Execucao manual concluida!');
      loadStatus();
      loadHealthScores();
      loadLogs(1, logsFilter);
      loadRuns();
    } catch (err) {
      setError(err.error || 'Erro na execucao');
    }
    setLoading(false);
  };

  // Risk level config
  const riskConfig = {
    critico: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Critico', icon: AlertTriangle },
    em_risco: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Em Risco', icon: TrendingDown },
    atencao: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', label: 'Atencao', icon: Eye },
    saudavel: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Saudavel', icon: Heart },
  };

  const RiskBadge = ({ level }) => {
    const rc = riskConfig[level] || riskConfig.atencao;
    return (
      <span style={{
        padding: '3px 10px', borderRadius: '6px', fontSize: '0.7rem',
        fontWeight: 600, background: rc.bg, color: rc.color,
        display: 'inline-flex', alignItems: 'center', gap: '4px'
      }}>
        <rc.icon size={11} /> {rc.label}
      </span>
    );
  };

  const HealthBar = ({ score, size = 'normal' }) => {
    const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : score >= 25 ? '#f97316' : '#ef4444';
    const height = size === 'small' ? '6px' : '10px';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        <div style={{ flex: 1, background: 'var(--card-border)', borderRadius: '99px', height, overflow: 'hidden' }}>
          <div style={{ width: `${score}%`, height: '100%', background: `linear-gradient(90deg, ${color}, ${color}cc)`, borderRadius: '99px', transition: 'width 0.5s ease' }} />
        </div>
        <span style={{ fontSize: size === 'small' ? '0.7rem' : '0.85rem', fontWeight: 700, color, minWidth: '30px' }}>{score}</span>
      </div>
    );
  };

  const campaignTypeLabels = {
    deposit_reminder: 'Lembrete Deposito',
    deposit_due_today: 'Vence Hoje',
    deposit_overdue: 'Deposito Atrasado',
    loan_reminder: 'Lembrete Parcela',
    loan_overdue: 'Parcela Atrasada',
    inactive: 'Reengajamento',
    goal_milestone: 'Meta Atingida',
    anniversary: 'Aniversario',
    health_drop: 'Queda no Score',
    pattern_change: 'Mudanca Padrao',
    onboarding: 'Boas-Vindas',
    deposit_streak: 'Sequencia',
    deposit_thanks: 'Agradecimento',
    balance_milestone: 'Marco de Saldo',
    finish_line: 'Reta Final',
  };

  const sortedHealthScores = [...healthScores].sort((a, b) => a.total_score - b.total_score);

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
              <Users size={19} /><span>Admin</span>
            </a>
            <a href="#emails" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('emails'); }}>
              <Mail size={19} /><span>E-mails</span>
            </a>
            <a href="#automation" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <Brain size={19} /><span>Automacao IA</span>
            </a>
            <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
              <Activity size={19} /><span>Atividade</span>
            </a>
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Historico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configuracoes</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main" style={{ maxWidth: '1100px' }}>
          {/* Page Header */}
          <div className="page-header">
            <div>
              <p className="page-greeting">Client Intelligence Engine</p>
              <h1 className="page-title">Automacao Inteligente</h1>
            </div>
            {status && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={handleToggle}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px', borderRadius: '10px', cursor: 'pointer',
                    border: 'none', fontWeight: 600, fontSize: '0.8rem',
                    background: status.enabled ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    color: status.enabled ? '#22c55e' : '#ef4444',
                  }}>
                  {status.enabled ? <><Zap size={14} /> Ativo</> : <><Pause size={14} /> Pausado</>}
                </button>
              </div>
            )}
          </div>

          {/* Section Navigation */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid var(--card-border)', paddingBottom: '0' }}>
            {[
              ['overview', 'Visao Geral', Activity],
              ['conversions', 'Conversoes', DollarSign],
              ['mind', 'Mente IA', Brain],
              ['health', 'Health Scores', Heart],
              ['logs', 'Historico IA', Clock],
              ['runs', 'Execucoes', Play]
            ].map(([key, label, Icon]) => (
              <button key={key} onClick={() => {
                setSection(key); setError(''); setSuccess('');
                if (key === 'overview') { loadStatus(); loadHealthScores(); }
                if (key === 'conversions') loadConversions();
                if (key === 'mind') loadPreview();
                if (key === 'health') loadHealthScores();
                if (key === 'logs') loadLogs(1, logsFilter);
                if (key === 'runs') loadRuns();
              }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  padding: '12px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: '0.84rem', fontWeight: section === key ? 600 : 400,
                  color: section === key ? '#3b82f6' : 'var(--text-color)',
                  borderBottom: section === key ? '2px solid #3b82f6' : '2px solid transparent',
                  transition: 'all 0.2s'
                }}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          {/* Alerts */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={15} /> {error}
            </div>
          )}
          {success && (
            <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={15} /> {success}
            </div>
          )}

          {/* ================================ */}
          {/* OVERVIEW SECTION                 */}
          {/* ================================ */}
          {section === 'overview' && status && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Engine Status Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: status.enabled ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Zap size={18} color={status.enabled ? '#22c55e' : '#ef4444'} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Status</div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: status.enabled ? '#22c55e' : '#ef4444' }}>
                        {status.enabled ? 'Ativo' : 'Pausado'}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Bell size={14} color='#3b82f6' />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Emails 24h</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-color)' }}>
                    {status.stats24h?.sent || 0}
                  </div>
                  {parseInt(status.stats24h?.failed || 0) > 0 && (
                    <div style={{ fontSize: '0.7rem', color: '#ef4444' }}>{status.stats24h.failed} falhas</div>
                  )}
                </div>

                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Target size={14} color='#8b5cf6' />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Campanhas (7d)</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-color)' }}>
                    {status.campaignsByType?.reduce((sum, c) => sum + parseInt(c.sent || 0), 0) || 0}
                  </div>
                </div>

                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Clock size={14} color='#f59e0b' />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Ultima Execucao</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-color)' }}>
                    {status.lastRun ? new Date(status.lastRun.created_at).toLocaleString('pt-BR') : 'Nunca'}
                  </div>
                </div>
              </div>

              {/* Risk Distribution */}
              <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={18} /> Mapa de Risco dos Clientes
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {Object.entries(riskConfig).map(([key, config]) => {
                    const count = status.riskDistribution?.[key] || 0;
                    const total = Object.values(status.riskDistribution || {}).reduce((s, v) => s + v, 0) || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={key} style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: config.bg }}>
                        <config.icon size={24} color={config.color} style={{ marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.8rem', fontWeight: 700, color: config.color }}>{count}</div>
                        <div style={{ fontSize: '0.72rem', color: config.color, fontWeight: 500 }}>{config.label}</div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '4px' }}>{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Campaigns by Type */}
              {status.campaignsByType?.length > 0 && (
                <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BarChart3 size={18} /> Campanhas por Tipo (7 dias)
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {status.campaignsByType.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '10px', background: 'var(--input-bg)' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-color)', flex: '1' }}>
                          {campaignTypeLabels[c.campaign_type] || c.campaign_type}
                        </span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#3b82f6' }}>{c.sent}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>enviados</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Controls */}
              <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Play size={18} /> Controles Manuais
                </h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleManualRun('health')} disabled={loading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--card-border)',
                      background: 'var(--input-bg)', color: 'var(--text-color)',
                      cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500
                    }}>
                    <Heart size={15} /> Atualizar Health Scores
                  </button>
                  <button onClick={() => handleManualRun('campaigns')} disabled={loading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--card-border)',
                      background: 'var(--input-bg)', color: 'var(--text-color)',
                      cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500
                    }}>
                    <Zap size={15} /> Executar Campanhas
                  </button>
                  <button onClick={() => handleManualRun('both')} disabled={loading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '10px 20px', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: '#fff',
                      cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600
                    }}>
                    <RefreshCw size={15} /> Executar Tudo
                  </button>
                </div>
                {status.config && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '10px', background: 'var(--input-bg)', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    <strong>Config:</strong> Max {status.config.maxEmailsPerDay}/dia, {status.config.maxEmailsPerWeek}/semana por cliente | Min {status.config.minHoursBetween}h entre emails | Health check: {status.config.healthCheckInterval} | Campanhas: {status.config.campaignCheckInterval}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================================ */}
          {/* CONVERSIONS + JOURNEYS SECTION   */}
          {/* ================================ */}
          {section === 'conversions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {journeys?.stats && (
                <>
                  {/* KPI Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                    <div style={{ padding: '20px', borderRadius: '14px', background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))', border: '1px solid rgba(34,197,94,0.2)' }}>
                      <div style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: 500, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DollarSign size={14} /> Receita por Email
                      </div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#22c55e' }}>
                        R$ {parseFloat(journeys.stats.revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Target size={14} /> Conversoes
                      </div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-color)' }}>{journeys.stats.total || 0}</div>
                    </div>
                    <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} /> Tempo Medio
                      </div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#8b5cf6' }}>{journeys.stats.avg_hours || '—'}h</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>email → deposito</div>
                    </div>
                    <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <BarChart3 size={14} /> Clicou → Converteu
                      </div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#f59e0b' }}>{journeys.stats.click_rate || 0}%</div>
                    </div>
                  </div>

                  {/* By Campaign Type */}
                  {journeys.byType?.length > 0 && (
                    <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 12px' }}>Por tipo de campanha</h3>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {journeys.byType.map((c, i) => (
                          <div key={i} style={{ padding: '10px 16px', borderRadius: '10px', background: 'var(--input-bg)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-color)' }}>{campaignTypeLabels[c.campaign_type] || c.campaign_type}</span>
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#22c55e' }}>R$ {parseFloat(c.revenue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{c.conversions}x | ~{c.avg_hours}h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Journey Timelines */}
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Activity size={18} /> Jornadas de Conversao
                  </h3>

                  {journeys.journeys?.length > 0 ? journeys.journeys.map((j, i) => {
                    const stepConfig = {
                      email_sent: { icon: '✉️', color: '#6b7280', label: 'Email enviado' },
                      email_opened: { icon: '👁️', color: '#22c55e', label: 'Abriu o email' },
                      email_clicked: { icon: '👆', color: '#3b82f6', label: 'Clicou no botao' },
                      link_click: { icon: '🔗', color: '#8b5cf6', label: 'Clicou em link' },
                      deposit: { icon: '💰', color: '#059669', label: 'Realizou deposito' },
                    };

                    return (
                      <div key={i} style={{
                        padding: '20px', borderRadius: '14px',
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                      }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <div>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-color)' }}>{j.user_name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                              {j.user_email} • {campaignTypeLabels[j.campaign_type] || j.campaign_type}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#22c55e' }}>
                              R$ {parseFloat(j.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: '#8b5cf6', fontWeight: 600 }}>
                              {j.hours_to_convert}h do email ao deposito
                            </div>
                          </div>
                        </div>

                        {/* Timeline */}
                        <div style={{ position: 'relative', paddingLeft: '28px' }}>
                          {/* Vertical line */}
                          <div style={{
                            position: 'absolute', left: '11px', top: '8px', bottom: '8px',
                            width: '2px', background: 'linear-gradient(to bottom, #6b7280, #22c55e, #059669)',
                            borderRadius: '2px'
                          }} />

                          {j.steps.map((step, si) => {
                            const cfg = stepConfig[step.type] || stepConfig.email_sent;
                            const isLast = si === j.steps.length - 1;
                            const prevTime = si > 0 ? new Date(j.steps[si - 1].time) : null;
                            const curTime = new Date(step.time);
                            const diffMin = prevTime ? Math.round((curTime - prevTime) / (1000 * 60)) : null;
                            const diffText = diffMin !== null ? (diffMin < 60 ? `+${diffMin}min` : `+${Math.round(diffMin / 6) / 10}h`) : null;

                            return (
                              <div key={si} style={{ position: 'relative', paddingBottom: isLast ? '0' : '16px' }}>
                                {/* Dot */}
                                <div style={{
                                  position: 'absolute', left: '-22px', top: '4px',
                                  width: '20px', height: '20px', borderRadius: '50%',
                                  background: isLast ? '#059669' : 'var(--card-bg)',
                                  border: `2px solid ${cfg.color}`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '10px', zIndex: 1,
                                }}>
                                  {cfg.icon}
                                </div>

                                {/* Content */}
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', minWidth: '130px' }}>
                                    {curTime.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                  </span>
                                  <span style={{ fontSize: '0.82rem', fontWeight: isLast ? 700 : 500, color: isLast ? '#059669' : 'var(--text-color)' }}>
                                    {cfg.label}
                                  </span>
                                  {step.detail && (
                                    <span style={{
                                      fontSize: '0.75rem', color: isLast ? '#059669' : '#3b82f6', fontWeight: isLast ? 700 : 400,
                                      maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                      {step.detail}
                                    </span>
                                  )}
                                  {diffText && (
                                    <span style={{
                                      fontSize: '0.62rem', padding: '1px 6px', borderRadius: '4px',
                                      background: 'rgba(139,92,246,0.08)', color: '#8b5cf6', fontWeight: 600
                                    }}>
                                      {diffText}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }) : (
                    <div style={{ padding: '40px 20px', textAlign: 'center', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                      <Target size={32} color='var(--text-muted)' style={{ marginBottom: '12px', opacity: 0.3 }} />
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>Nenhuma conversao detectada ainda. Conversoes sao registradas quando um cliente deposita ate 72h apos receber um email automatico.</p>
                    </div>
                  )}
                </>
              )}

              {!journeys && (
                <div style={{ padding: '60px 20px', textAlign: 'center', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <RefreshCw size={24} color='var(--text-muted)' style={{ marginBottom: '12px', animation: 'spin 1s linear infinite' }} />
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Analisando conversoes...</p>
                </div>
              )}
            </div>
          )}

          {/* ================================ */}
          {/* MIND / PREVIEW SECTION           */}
          {/* ================================ */}
          {section === 'mind' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Header com refresh */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Brain size={20} /> O que o Engine esta pensando agora
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Simulacao em tempo real do proximo ciclo — nenhum email e enviado aqui
                  </p>
                </div>
                <button onClick={loadPreview} disabled={previewLoading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px', borderRadius: '10px', border: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff',
                    cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                    opacity: previewLoading ? 0.6 : 1,
                  }}>
                  <RefreshCw size={14} className={previewLoading ? 'spin' : ''} />
                  {previewLoading ? 'Analisando...' : 'Atualizar Analise'}
                </button>
              </div>

              {/* Summary Cards */}
              {preview?.summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                  {[
                    { label: 'Clientes', value: preview.summary.total_clients, color: 'var(--text-color)', icon: Users },
                    { label: 'Vai Enviar', value: preview.summary.will_send, color: '#22c55e', icon: Zap },
                    { label: 'Aguardando', value: preview.summary.waiting_time, color: '#f59e0b', icon: Clock },
                    { label: 'Sem Acao', value: preview.summary.no_action, color: '#6b7280', icon: Check },
                    { label: 'Bloqueados', value: preview.summary.blocked, color: '#ef4444', icon: Shield },
                  ].map((item, i) => (
                    <div key={i} style={{
                      padding: '14px', borderRadius: '12px',
                      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                      textAlign: 'center'
                    }}>
                      <item.icon size={16} color={item.color} />
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: item.color, margin: '4px 0' }}>{item.value}</div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Thoughts List */}
              {preview?.thoughts?.length > 0 ? (
                preview.thoughts.map((thought, i) => {
                  const actionColors = {
                    enviar_email: { bg: 'rgba(34,197,94,0.08)', border: '#22c55e', label: 'VAI ENVIAR', icon: Zap },
                    aguardar_horario: { bg: 'rgba(245,158,11,0.08)', border: '#f59e0b', label: 'AGUARDANDO', icon: Clock },
                    nenhuma: { bg: 'var(--card-bg)', border: 'var(--card-border)', label: 'SEM ACAO', icon: Check },
                    bloqueado: { bg: 'rgba(239,68,68,0.08)', border: '#ef4444', label: 'BLOQUEADO', icon: Shield },
                    erro: { bg: 'rgba(239,68,68,0.08)', border: '#ef4444', label: 'ERRO', icon: AlertTriangle },
                  };
                  const ac = actionColors[thought.next_action?.action] || actionColors.nenhuma;
                  const rc = riskConfig[thought.risk_level] || riskConfig.atencao;

                  return (
                    <div key={i} style={{
                      padding: '20px', borderRadius: '14px',
                      background: ac.bg, border: `1px solid ${ac.border}`,
                      transition: 'all 0.2s'
                    }}>
                      {/* Client Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-color)' }}>{thought.name}</span>
                            <RiskBadge level={thought.risk_level} />
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{thought.email}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '4px 12px', borderRadius: '8px', fontSize: '0.7rem',
                            fontWeight: 700, background: `${ac.border}20`, color: ac.border,
                            letterSpacing: '0.5px'
                          }}>
                            <ac.icon size={12} /> {ac.label}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                            Score: <strong style={{ color: rc.color }}>{thought.health_score}</strong>/100
                          </div>
                        </div>
                      </div>

                      {/* Health Breakdown Mini */}
                      {thought.health_breakdown && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
                          {[
                            { k: 'deposit_regularity', label: 'Dep', max: 30 },
                            { k: 'email_engagement', label: 'Email', max: 20 },
                            { k: 'login_frequency', label: 'Login', max: 15 },
                            { k: 'balance_trend', label: 'Saldo', max: 15 },
                            { k: 'goal_progress', label: 'Meta', max: 10 },
                            { k: 'loyalty', label: 'Leal', max: 10 },
                          ].map(({ k, label, max }) => {
                            const val = thought.health_breakdown[k] || 0;
                            const pct = Math.round((val / max) * 100);
                            const c = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
                            return (
                              <div key={k} style={{
                                padding: '4px 8px', borderRadius: '6px', fontSize: '0.62rem',
                                background: 'var(--input-bg)', display: 'flex', alignItems: 'center', gap: '4px'
                              }}>
                                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                                <span style={{ fontWeight: 700, color: c }}>{val}/{max}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Reasoning - O pensamento do engine */}
                      <div style={{
                        padding: '12px 14px', borderRadius: '10px',
                        background: 'var(--input-bg)', marginBottom: '12px',
                        borderLeft: `3px solid ${ac.border}`
                      }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Raciocinio do Engine
                        </div>
                        {thought.reasoning.map((r, j) => (
                          <div key={j} style={{
                            fontSize: '0.78rem', color: 'var(--text-color)',
                            padding: '3px 0', display: 'flex', alignItems: 'flex-start', gap: '6px'
                          }}>
                            <span style={{ color: r.includes('ENVIAR') ? '#22c55e' : r.includes('NAO') ? '#ef4444' : r.includes('AGUARDAR') ? '#f59e0b' : '#8b5cf6', fontSize: '0.65rem', marginTop: '2px' }}>
                              {r.includes('ENVIAR') || r.includes('Atingiu') ? '>' : r.includes('NAO') ? 'x' : r.includes('AGUARDAR') ? '~' : '>'}
                            </span>
                            <span>{r}</span>
                          </div>
                        ))}
                      </div>

                      {/* Email Preview */}
                      {thought.decisions?.filter(d => d.email_preview).map((d, j) => (
                        <div key={j} style={{
                          padding: '12px 14px', borderRadius: '10px',
                          background: 'rgba(59,130,246,0.05)', border: '1px dashed rgba(59,130,246,0.3)',
                        }}>
                          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#3b82f6', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Mail size={12} /> Preview do Email
                          </div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-color)', marginBottom: '4px' }}>
                            {d.email_preview.subject}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            {d.email_preview.body_preview}
                          </div>
                          {d.optimal_hour !== undefined && (
                            <div style={{ fontSize: '0.68rem', color: '#8b5cf6', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={11} /> Horario ideal: {d.optimal_hour}h
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Signals detected */}
                      {thought.signals?.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                          {thought.signals.map((sig, j) => (
                            <span key={j} style={{
                              padding: '3px 8px', borderRadius: '6px', fontSize: '0.62rem', fontWeight: 600,
                              background: sig.priority === 'critical' ? 'rgba(239,68,68,0.1)' : sig.priority === 'high' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
                              color: sig.priority === 'critical' ? '#ef4444' : sig.priority === 'high' ? '#f59e0b' : '#3b82f6'
                            }}>
                              {campaignTypeLabels[sig.type] || sig.type}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{
                  padding: '60px 20px', textAlign: 'center', borderRadius: '14px',
                  background: 'var(--card-bg)', border: '1px solid var(--card-border)'
                }}>
                  {previewLoading ? (
                    <>
                      <Brain size={40} color='#8b5cf6' style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <p style={{ color: '#8b5cf6', fontSize: '0.95rem', fontWeight: 600 }}>Analisando cada cliente...</p>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>O engine esta calculando health scores, detectando sinais e simulando decisoes</p>
                    </>
                  ) : (
                    <>
                      <Brain size={40} color='var(--text-muted)' style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Clique em "Atualizar Analise" para ver o que o engine esta pensando</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ================================ */}
          {/* HEALTH SCORES SECTION            */}
          {/* ================================ */}
          {section === 'health' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                {Object.entries(riskConfig).map(([key, config]) => {
                  const count = sortedHealthScores.filter(s => s.risk_level === key).length;
                  return (
                    <div key={key} style={{ padding: '16px', borderRadius: '12px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', textAlign: 'center' }}>
                      <config.icon size={20} color={config.color} />
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: config.color, margin: '4px 0' }}>{count}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{config.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Client List */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  padding: '12px 18px', fontSize: '0.72rem', fontWeight: 600,
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)',
                  textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  <span>Cliente</span>
                  <span>Health Score</span>
                  <span>Risco</span>
                  <span>Acao</span>
                </div>

                {sortedHealthScores.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Nenhum health score calculado ainda. Execute "Atualizar Health Scores" na aba Visao Geral.
                  </div>
                ) : (
                  sortedHealthScores.map((score, i) => (
                    <div key={score.user_id} style={{
                      display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                      padding: '14px 18px', alignItems: 'center',
                      borderBottom: i < sortedHealthScores.length - 1 ? '1px solid var(--card-border)' : 'none',
                      background: selectedUser === score.user_id ? 'rgba(59,130,246,0.05)' : 'transparent'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-color)' }}>{score.name}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{score.email}</div>
                      </div>
                      <div style={{ paddingRight: '16px' }}>
                        <HealthBar score={score.total_score} size="small" />
                      </div>
                      <div><RiskBadge level={score.risk_level} /></div>
                      <div>
                        <button onClick={() => loadUserDetail(score.user_id)}
                          disabled={detailLoading && selectedUser === score.user_id}
                          style={{
                            padding: '5px 12px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer',
                            border: selectedUser === score.user_id ? '2px solid #3b82f6' : '1px solid var(--card-border)',
                            background: selectedUser === score.user_id ? 'rgba(59,130,246,0.1)' : 'transparent',
                            color: '#3b82f6',
                          }}>
                          {detailLoading && selectedUser === score.user_id ? 'Carregando...' : selectedUser === score.user_id ? 'Aberto' : 'Detalhes'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* User Detail - Loading */}
              {selectedUser && detailLoading && (
                <div ref={detailRef} style={{ padding: '40px', borderRadius: '14px', background: 'var(--card-bg)', border: '2px solid #3b82f6', textAlign: 'center' }}>
                  <RefreshCw size={24} color='#3b82f6' style={{ marginBottom: '12px', animation: 'spin 1s linear infinite' }} />
                  <p style={{ color: '#3b82f6', fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>Calculando health score em tempo real...</p>
                </div>
              )}

              {/* User Detail Modal */}
              {selectedUser && userDetail && !detailLoading && (
                <div ref={detailRef} style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '2px solid #3b82f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <UserCheck size={18} /> Analise Detalhada
                    </h3>
                    <button onClick={() => { setSelectedUser(null); setUserDetail(null); }}
                      style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Fechar
                    </button>
                  </div>

                  {/* Score Breakdown */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'Score Total', value: userDetail.current.total_score, max: 100 },
                      { label: 'Depositos', value: userDetail.current.scores.deposit_regularity, max: 30 },
                      { label: 'Email Engage', value: userDetail.current.scores.email_engagement, max: 20 },
                      { label: 'Login Freq.', value: userDetail.current.scores.login_frequency, max: 15 },
                      { label: 'Tendencia Saldo', value: userDetail.current.scores.balance_trend, max: 15 },
                      { label: 'Meta', value: userDetail.current.scores.goal_progress, max: 10 },
                      { label: 'Lealdade', value: userDetail.current.scores.loyalty, max: 10 },
                    ].map((item, i) => (
                      <div key={i} style={{ padding: '14px', borderRadius: '10px', background: 'var(--input-bg)' }}>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '6px' }}>{item.label}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-color)', marginBottom: '4px' }}>
                          {item.value}<span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>/{item.max}</span>
                        </div>
                        <HealthBar score={Math.round((item.value / item.max) * 100)} size="small" />
                      </div>
                    ))}
                  </div>

                  {/* Active Signals */}
                  {userDetail.signals?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertTriangle size={15} /> Sinais Ativos
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {userDetail.signals.map((sig, i) => (
                          <div key={i} style={{
                            padding: '10px 14px', borderRadius: '8px', background: 'var(--input-bg)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                          }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-color)' }}>
                              {campaignTypeLabels[sig.type] || sig.type}
                            </span>
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                              background: sig.priority === 'critical' ? 'rgba(239,68,68,0.1)' : sig.priority === 'high' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
                              color: sig.priority === 'critical' ? '#ef4444' : sig.priority === 'high' ? '#f59e0b' : '#3b82f6'
                            }}>
                              {sig.priority}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Automation History for User */}
                  {userDetail.automationLogs?.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-color)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Mail size={15} /> Emails Automaticos Enviados
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {userDetail.automationLogs.map((log, i) => {
                          const isExpanded = expandedLog === log.id;
                          const statusColor = log.email_status === 'clicked' ? '#3b82f6' : log.email_status === 'opened' ? '#22c55e' : '#f59e0b';
                          const statusLabel = log.email_status === 'clicked' ? 'Clicou' : log.email_status === 'opened' ? 'Abriu' : 'Enviado';
                          return (
                            <div key={i} style={{
                              borderRadius: '10px', background: 'var(--input-bg)', overflow: 'hidden',
                              border: isExpanded ? '1px solid #3b82f6' : '1px solid transparent',
                              transition: 'all 0.2s'
                            }}>
                              {/* Row Header - Clickable */}
                              <div onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                                style={{
                                  padding: '12px 14px', cursor: 'pointer',
                                  display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 0.8fr 0.6fr 24px', gap: '8px', alignItems: 'center'
                                }}>
                                <span style={{
                                  padding: '3px 8px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 600,
                                  background: log.signal_priority === 'critical' ? 'rgba(239,68,68,0.1)' : log.signal_priority === 'high' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
                                  color: log.signal_priority === 'critical' ? '#ef4444' : log.signal_priority === 'high' ? '#f59e0b' : '#3b82f6',
                                  width: 'fit-content'
                                }}>
                                  {campaignTypeLabels[log.campaign_type] || log.campaign_type}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-color)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {log.email_subject || '—'}
                                </span>
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                  {new Date(log.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span style={{ fontSize: '0.68rem', fontWeight: 600, color: statusColor }}>
                                  {statusLabel}
                                </span>
                                <ChevronRight size={14} style={{
                                  color: 'var(--text-muted)',
                                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }} />
                              </div>

                              {/* Expanded Email Body */}
                              {isExpanded && (
                                <div style={{ borderTop: '1px solid var(--card-border)' }}>
                                  {/* Subject bar */}
                                  <div style={{
                                    padding: '10px 14px', background: 'rgba(59,130,246,0.04)',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                  }}>
                                    <Mail size={13} color='#3b82f6' />
                                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-color)' }}>
                                      {log.email_subject}
                                    </span>
                                    <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                                      {log.opened_at ? 'Aberto em ' + new Date(log.opened_at).toLocaleString('pt-BR') : 'Nao aberto ainda'}
                                    </span>
                                  </div>

                                  {/* Email Full Preview via iframe */}
                                  {log.email_log_id ? (
                                    <div style={{ background: '#f0f2f5', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                                      <iframe
                                        title="Email Preview"
                                        src={`https://app.solvere.pro/api/email/preview/${log.email_log_id}`}
                                        style={{ width: '100%', height: '550px', border: 'none', background: '#f0f2f5' }}
                                      />
                                    </div>
                                  ) : log.email_body ? (
                                    <div style={{ padding: '16px', background: '#ffffff', borderRadius: '0 0 10px 10px', maxHeight: '400px', overflow: 'auto' }}>
                                      <div dangerouslySetInnerHTML={{ __html: log.email_body }} />
                                    </div>
                                  ) : (
                                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                      Corpo do email nao disponivel
                                    </div>
                                  )}

                                  {/* Metadata */}
                                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                                    <div style={{
                                      padding: '8px 14px', background: 'var(--input-bg)',
                                      fontSize: '0.65rem', color: 'var(--text-muted)',
                                      display: 'flex', gap: '12px', flexWrap: 'wrap'
                                    }}>
                                      {Object.entries(log.metadata).map(([k, v]) => (
                                        <span key={k}><strong>{k}:</strong> {String(v)}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ================================ */}
          {/* AUTOMATION LOGS SECTION           */}
          {/* ================================ */}
          {section === 'logs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Filters */}
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {[['', 'Todos'], ...Object.entries(campaignTypeLabels)].map(([key, label]) => (
                  <button key={key} onClick={() => { setLogsFilter(key); setLogsPage(1); loadLogs(1, key); }}
                    style={{
                      padding: '6px 12px', borderRadius: '8px', fontSize: '0.72rem', cursor: 'pointer',
                      border: logsFilter === key ? '2px solid #3b82f6' : '1px solid var(--card-border)',
                      background: logsFilter === key ? 'rgba(59,130,246,0.08)' : 'transparent',
                      color: logsFilter === key ? '#3b82f6' : 'var(--text-color)',
                      fontWeight: logsFilter === key ? 600 : 400
                    }}>{label}</button>
                ))}
              </div>

              {/* Log List */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                  padding: '12px 18px', fontSize: '0.72rem', fontWeight: 600,
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)',
                  textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  <span>Cliente</span>
                  <span>Campanha</span>
                  <span>Prioridade</span>
                  <span>Status</span>
                  <span>Data</span>
                </div>

                {automationLogs.logs.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Nenhum log encontrado
                  </div>
                ) : (
                  automationLogs.logs.map((log, i) => (
                    <div key={log.id} style={{
                      display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                      padding: '12px 18px', alignItems: 'center',
                      borderBottom: i < automationLogs.logs.length - 1 ? '1px solid var(--card-border)' : 'none'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-color)' }}>{log.user_name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{log.user_email}</div>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-color)' }}>
                        {campaignTypeLabels[log.campaign_type] || log.campaign_type}
                      </span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                        display: 'inline-block', width: 'fit-content',
                        background: log.signal_priority === 'critical' ? 'rgba(239,68,68,0.1)' : log.signal_priority === 'high' ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
                        color: log.signal_priority === 'critical' ? '#ef4444' : log.signal_priority === 'high' ? '#f59e0b' : '#3b82f6'
                      }}>
                        {log.signal_priority}
                      </span>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 600,
                        color: log.email_status === 'clicked' ? '#3b82f6' : log.email_status === 'opened' ? '#22c55e' : log.status === 'failed' ? '#ef4444' : '#f59e0b'
                      }}>
                        {log.status === 'failed' ? 'Falha' : log.email_status === 'clicked' ? 'Clicou' : log.email_status === 'opened' ? 'Abriu' : 'Enviado'}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {new Date(log.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {automationLogs.total > 25 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <button onClick={() => { const p = Math.max(1, logsPage - 1); setLogsPage(p); loadLogs(p, logsFilter); }}
                    disabled={logsPage === 1}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    Anterior
                  </button>
                  <span style={{ padding: '6px 14px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {logsPage} / {Math.ceil(automationLogs.total / 25)}
                  </span>
                  <button onClick={() => { const p = logsPage + 1; setLogsPage(p); loadLogs(p, logsFilter); }}
                    disabled={logsPage >= Math.ceil(automationLogs.total / 25)}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    Proximo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ================================ */}
          {/* RUNS HISTORY SECTION              */}
          {/* ================================ */}
          {section === 'runs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-color)' }}>Historico de Execucoes</h3>
                <button onClick={loadRuns}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem' }}>
                  <RefreshCw size={13} /> Atualizar
                </button>
              </div>

              <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1.5fr',
                  padding: '12px 18px', fontSize: '0.72rem', fontWeight: 600,
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)',
                  textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  <span>Tipo</span>
                  <span>Enviados</span>
                  <span>Pulados</span>
                  <span>Erros</span>
                  <span>Data/Hora</span>
                </div>

                {automationRuns.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Nenhuma execucao registrada
                  </div>
                ) : (
                  automationRuns.map((run, i) => (
                    <div key={run.id} style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1.5fr',
                      padding: '12px 18px', alignItems: 'center',
                      borderBottom: i < automationRuns.length - 1 ? '1px solid var(--card-border)' : 'none'
                    }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-color)' }}>
                        {run.type === 'campaign' ? 'Campanhas' : run.type}
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#22c55e' }}>{run.emails_sent}</span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{run.emails_skipped}</span>
                      <span style={{ fontSize: '0.85rem', color: run.errors > 0 ? '#ef4444' : 'var(--text-muted)' }}>{run.errors}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(run.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AutomationPage;
